import { useEffect, useRef, useState } from "react";
import { Plus, Trash, PencilSimple, FileText, UploadSimple, FileArrowDown, Bank as BankIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LAYOUTS = [
  { key: "open_land", label: "Open Land", hint: "Land valuation only — the Building (Part B) section is omitted" },
  { key: "land_building", label: "Land & Building", hint: "Full report — Land + Building + all parts" },
  { key: "industrial_land", label: "Industrial Land", hint: "Industrial plot with structures — Land + Building + all parts" },
  { key: "flat", label: "Flats", hint: "Flat / apartment valuation (full layout)" },
];
const LAYOUT_LABELS = Object.fromEntries(LAYOUTS.map((l) => [l.key, l.label]));
const EMPTY = { name: "", layout_key: "land_building", banks: [], notes: "" };

export default function ReportFormats() {
  const [items, setItems] = useState(null);
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const fileRefs = useRef({});

  const load = () => api.get("/report-formats").then((r) => setItems(r.data)).catch(() => setItems([]));
  const loadBanks = () => api.get("/banks/empanelled").then((r) => setBanks((r.data || []).map((b) => b.bank_name))).catch(() => setBanks([]));
  useEffect(() => { load(); loadBanks(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const toggleBank = (b) => {
    const cur = form.banks || [];
    setForm({ ...form, banks: cur.includes(b) ? cur.filter((x) => x !== b) : [...cur, b] });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/report-formats/${editing}`, form);
      else await api.post("/report-formats", form);
      toast.success(editing ? "Format updated" : "Format added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/report-formats/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadTemplate = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/report-formats/${id}/template`, fd);
      toast.success("Template file uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
  };

  if (!items) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading formats…</div>;

  return (
    <div className="space-y-6" data-testid="report-formats-page">
      <PageHeader
        overline="Valuation Data" title="Bank Report Formats"
        subtitle="Define each bank's report formats (Open Land, Land & Building, Industrial Land, Flats) and keep a sample on record. The chosen format drives how the valuation report prints."
        action={<Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="add-format-btn"><Plus size={16} className="mr-1" /> Add Format</Button>}
      />

      {banks.length === 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-800" data-testid="no-empanelled-note">
          No banks yet. Add a bank branch (Bank Branches) so it becomes available to assign formats.
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState icon={FileText}>No report formats yet. Add a format and assign it to one or more empanelled banks.</EmptyState>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...items].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((t) => (
            <Card key={t.id} className="p-5 border-border transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`format-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><FileText size={18} weight="duotone" className="text-accent shrink-0" /><h3 className="font-display font-semibold text-lg text-primary truncate">{t.name}</h3></div>
                  <div className="text-xs text-accent mt-0.5">{LAYOUT_LABELS[t.layout_key] || t.layout_key}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => { setEditing(t.id); setForm({ ...EMPTY, ...t, banks: t.banks || [] }); setOpen(true); }} data-testid={`edit-format-${t.id}`}><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={() => remove(t.id)} data-testid={`delete-format-${t.id}`}><Trash size={14} /></Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mt-3">
                {(t.banks || []).length === 0
                  ? <span className="text-[11px] text-muted-foreground">Not assigned to any bank yet</span>
                  : (t.banks || []).map((b) => <span key={b} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground"><BankIcon size={11} /> {b}</span>)}
              </div>

              {t.notes && <p className="text-sm text-muted-foreground mt-3 line-clamp-3 whitespace-pre-wrap">{t.notes}</p>}

              <div className="mt-4 flex items-center gap-2 border-t border-border pt-3">
                <input ref={(el) => (fileRefs.current[t.id] = el)} type="file" className="hidden" data-testid={`format-file-${t.id}`}
                  onChange={(e) => { uploadTemplate(t.id, e.target.files?.[0]); e.target.value = ""; }} />
                <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => fileRefs.current[t.id]?.click()} data-testid={`upload-format-${t.id}`}>
                  <UploadSimple size={14} className="mr-1" /> {t.template_filename ? "Replace file" : "Upload sample"}
                </Button>
                {t.template_filename && (
                  <a href={`${API}/report-formats/${t.id}/template`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-accent hover:underline" data-testid={`download-format-${t.id}`}>
                    <FileArrowDown size={14} /> {t.template_filename}
                  </a>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit Format" : "Add Report Format"}</DialogTitle>
            <DialogDescription>Name the format, pick its layout, and tick the empanelled banks that use it.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="format-form">
            <div className="space-y-1.5"><Label>Format Name *</Label><Input required value={form.name} onChange={set("name")} placeholder="e.g. SBI Open Land Format" data-testid="format-name" /></div>
            <div className="space-y-1.5"><Label>Layout</Label>
              <Select value={form.layout_key} onValueChange={set("layout_key")}>
                <SelectTrigger data-testid="format-layout"><SelectValue /></SelectTrigger>
                <SelectContent>{LAYOUTS.map((l) => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">{LAYOUTS.find((l) => l.key === form.layout_key)?.hint}</p>
            </div>
            <div>
              <Label className="mb-2 block">Assign to Empanelled Banks</Label>
              {banks.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">No banks available yet. Add a bank branch first.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {banks.map((b) => {
                    const on = (form.banks || []).includes(b);
                    return (
                      <button type="button" key={b} onClick={() => toggleBank(b)} data-testid={`format-bank-${b.replace(/\W+/g, "-")}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-150 ${on ? "bg-accent text-accent-foreground border-accent" : "bg-white text-muted-foreground border-border hover:border-accent"}`}>
                        {b}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={set("notes")} placeholder="Internal notes about this format" data-testid="format-notes" /></div>
            <DialogFooter><Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-format-btn">{editing ? "Update" : "Add"} Format</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
